import React, { useState, useCallback } from 'react';
import { 
  Download, 
  FileSpreadsheet, 
  FileText, 
  Calendar, 
  Building2, 
  Filter,
  CheckCircle,
  AlertTriangle,
  XCircle,
  Loader2,
  Wrench,
  Shield,
  Users
} from 'lucide-react';
import toast from '../../../utils/toast';
import PillSelect from '../../../components/UI/PillSelect';
import { StatusBadge } from '../../../components/UI/StatusBadge';

/**
 * ComplianceReport - Generate and export MOM compliance reports
 * 
 * Features:
 * - Month selector
 * - Department filter
 * - Risk level filter
 * - Export to Excel/PDF
 */
const ComplianceReport = ({ 
  getComplianceReport,
  employees,
  stats,
  loading: initialLoading
}) => {
  const [loading, setLoading] = useState(false);
  const [reportData, setReportData] = useState(null);
  const [filters, setFilters] = useState({
    month: new Date().toISOString().slice(0, 7), // Current month YYYY-MM
    department: '',
    status: '',
    trade: ''
  });

  // Generate report
  const handleGenerateReport = useCallback(async () => {
    setLoading(true);
    try {
      const data = await getComplianceReport(filters);
      setReportData(data);
      toast.success('✅ Report generated successfully');
    } catch (error) {
      console.error('Error generating report:', error);
      toast.error('❌ Failed to generate report');
    } finally {
      setLoading(false);
    }
  }, [getComplianceReport, filters]);

  // Export to Excel (CSV format)
  const handleExportExcel = useCallback(() => {
    if (!reportData?.records?.length) {
      toast.error('❌ No data to export');
      return;
    }

    try {
      // Build CSV content
      const headers = [
        'Staff ID',
        'Employee Name',
        'Department',
        'Trade',
        'Classification',
        'OT Hours',
        'Working Days with OT',
        'Compliance Status',
        'Hours to 72h Limit'
      ];
      
      const rows = reportData.records.map(record => [
        record.employeeId,
        record.employeeName,
        record.department,
        record.trade,
        record.classification,
        record.totalHours,
        record.workingDays,
        record.complianceStatus,
        record.thresholdHours
      ]);

      const csvContent = [
        headers.join(','),
        ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
      ].join('\n');

      // Create and download file
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = `overtime-compliance-report-${filters.month}.csv`;
      link.click();
      
      toast.success('✅ Excel export downloaded');
    } catch (error) {
      console.error('Error exporting:', error);
      toast.error('❌ Failed to export');
    }
  }, [reportData, filters.month]);

  // Get unique departments from current data
  const departments = [...new Set(employees?.map(e => e.department).filter(Boolean))].sort();

  // Get unique trades from current data
  const uniqueTrades = [...new Set(employees?.map(e => e.trade).filter(Boolean).filter(t => t !== 'No Trade'))].sort();

  // Get month options (current + last 11 months)
  const getMonthOptions = () => {
    const options = [];
    const now = new Date();
    for (let i = 0; i < 12; i++) {
      const date = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const value = date.toISOString().slice(0, 7);
      const label = date.toLocaleDateString('en-SG', { month: 'long', year: 'numeric' });
      options.push({ value, label });
    }
    return options;
  };

  const monthOptions = getMonthOptions();

  return (
    <div className="space-y-6">
      {/* Report Generation Controls */}
      <div className="bg-white rounded-2xl shadow-sm border p-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">Generate Compliance Report</h3>
            <p className="text-sm text-gray-600 mt-1">
              Create MOM-ready overtime compliance reports with filters
            </p>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={handleGenerateReport}
              disabled={loading}
              className="inline-flex items-center px-5 py-2.5 text-sm font-medium text-white bg-red-600 rounded-full hover:bg-red-700 transition-colors disabled:opacity-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-500 focus-visible:ring-offset-2"
            >
              {loading ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <FileText className="w-4 h-4 mr-2" />
              )}
              Generate Report
            </button>
          </div>
        </div>

        {/* Filters */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {/* Month Filter */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <Calendar className="w-4 h-4 inline mr-1" />
              Report Month
            </label>
            <PillSelect
              options={monthOptions}
              value={filters.month}
              onChange={(val) => setFilters(prev => ({ ...prev, month: val }))}
              theme="admin"
              icon={Calendar}
              iconColor="text-red-600"
              className="w-full"
            />
          </div>

          {/* Department Filter */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <Building2 className="w-4 h-4 inline mr-1" />
              Department
            </label>
            <PillSelect
              options={[
                { value: '', label: 'All Departments' },
                ...departments.map(dept => ({ value: dept, label: dept }))
              ]}
              value={filters.department}
              onChange={(val) => setFilters(prev => ({ ...prev, department: val }))}
              theme="admin"
              icon={Building2}
              iconColor="text-red-600"
              className="w-full"
            />
          </div>

          {/* Trade Filter */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <Filter className="w-4 h-4 inline mr-1" />
              Trade
            </label>
            <PillSelect
              options={[
                { value: '', label: 'All Trades' },
                ...uniqueTrades.map(trade => ({ value: trade, label: trade }))
              ]}
              value={filters.trade}
              onChange={(val) => setFilters(prev => ({ ...prev, trade: val }))}
              theme="admin"
              icon={Wrench}
              iconColor="text-red-600"
              className="w-full"
            />
          </div>

          {/* Status Filter */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <Filter className="w-4 h-4 inline mr-1" />
              Compliance Status
            </label>
            <PillSelect
              options={[
                { value: '', label: 'All Statuses' },
                { value: 'compliant', label: 'Compliant' },
                { value: 'at_risk', label: 'At Risk (48-60h)' },
                { value: 'critical', label: 'Critical (60-72h)' },
                { value: 'non_compliant', label: 'Non-Compliant (>72h)' },
              ]}
              value={filters.status}
              onChange={(val) => setFilters(prev => ({ ...prev, status: val }))}
              theme="admin"
              icon={Shield}
              iconColor="text-red-600"
              className="w-full"
            />
          </div>
        </div>
      </div>

      {/* Report Results */}
      {reportData ? (
        <div className="space-y-6">
          {/* Summary Cards — admin metrics-card pattern (DS §Metrics Cards) */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-6">
            <div className="bg-white rounded-2xl shadow-sm border p-4 sm:p-6">
              <div className="flex items-center">
                <div className="w-9 h-9 sm:w-12 sm:h-12 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <Users className="w-4 h-4 sm:w-6 sm:h-6 text-blue-600" />
                </div>
                <div className="ml-2 sm:ml-4 min-w-0">
                  <p className="text-lg sm:text-2xl font-bold text-gray-900">{reportData.summary?.totalEmployees || 0}</p>
                  <p className="text-xs sm:text-sm text-gray-600 truncate">Total Employees</p>
                </div>
              </div>
            </div>
            <div className="bg-white rounded-2xl shadow-sm border p-4 sm:p-6">
              <div className="flex items-center">
                <div className="w-9 h-9 sm:w-12 sm:h-12 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <CheckCircle className="w-4 h-4 sm:w-6 sm:h-6 text-green-600" />
                </div>
                <div className="ml-2 sm:ml-4 min-w-0">
                  <p className="text-lg sm:text-2xl font-bold text-gray-900">{reportData.summary?.compliantCount || 0}</p>
                  <p className="text-xs sm:text-sm text-gray-600 truncate">Compliant</p>
                </div>
              </div>
            </div>
            <div className="bg-white rounded-2xl shadow-sm border p-4 sm:p-6">
              <div className="flex items-center">
                <div className="w-9 h-9 sm:w-12 sm:h-12 bg-orange-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <AlertTriangle className="w-4 h-4 sm:w-6 sm:h-6 text-orange-600" />
                </div>
                <div className="ml-2 sm:ml-4 min-w-0">
                  <p className="text-lg sm:text-2xl font-bold text-gray-900">
                    {(reportData.summary?.atRiskCount || 0) + (reportData.summary?.criticalCount || 0)}
                  </p>
                  <p className="text-xs sm:text-sm text-gray-600 truncate">At Risk / Critical</p>
                </div>
              </div>
            </div>
            <div className="bg-white rounded-2xl shadow-sm border p-4 sm:p-6">
              <div className="flex items-center">
                <div className="w-9 h-9 sm:w-12 sm:h-12 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <XCircle className="w-4 h-4 sm:w-6 sm:h-6 text-red-600" />
                </div>
                <div className="ml-2 sm:ml-4 min-w-0">
                  <p className="text-lg sm:text-2xl font-bold text-gray-900">{reportData.summary?.nonCompliantCount || 0}</p>
                  <p className="text-xs sm:text-sm text-gray-600 truncate">Non-Compliant</p>
                </div>
              </div>
            </div>
          </div>

          {/* Export Actions */}
          <div className="bg-white rounded-2xl shadow-sm border p-4">
            <div className="flex items-center justify-between">
              <div className="text-sm text-gray-600">
                <span className="font-medium text-gray-900">{reportData.records?.length || 0}</span> records in report
                <span className="mx-2">•</span>
                Compliance Rate: <span className="font-medium text-gray-900">{reportData.summary?.complianceRate || 0}%</span>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={handleExportExcel}
                  className="inline-flex items-center px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-full hover:bg-gray-50 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-500 focus-visible:ring-offset-1"
                >
                  <FileSpreadsheet className="w-4 h-4 mr-2 text-green-600" />
                  Export Excel
                </button>
              </div>
            </div>
          </div>

          {/* Report Table */}
          <div className="bg-white rounded-2xl shadow-sm border overflow-hidden">
            <div className="px-4 py-3 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">Detailed Report</h3>
            </div>
            {/* Mobile card view */}
            <div className={`md:hidden ${reportData.records?.length ? 'p-4 space-y-3' : ''}`}>
              {reportData.records?.map(record => (
                <div key={record.employeeId} className="bg-white border border-gray-200 rounded-xl p-4">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-gray-900 truncate">{record.employeeName}</p>
                      <p className="text-xs text-gray-500">{record.employeeId}</p>
                    </div>
                    <div className="flex-shrink-0">
                      <StatusBadge status={record.complianceStatus} showIcon />
                    </div>
                  </div>
                  <div className="mt-3 space-y-2">
                    <div className="flex items-start justify-between gap-3 text-sm">
                      <span className="text-gray-500 flex-shrink-0">Department</span>
                      <span className="text-gray-900 text-right truncate min-w-0">{record.department}</span>
                    </div>
                    <div className="flex items-start justify-between gap-3 text-sm">
                      <span className="text-gray-500 flex-shrink-0">Trade</span>
                      <span className="text-gray-900 text-right truncate min-w-0">{record.trade}</span>
                    </div>
                    <div className="flex items-start justify-between gap-3 text-sm">
                      <span className="text-gray-500 flex-shrink-0">OT Hours</span>
                      <span className="text-gray-900 text-right truncate min-w-0 tabular-nums font-medium">{record.totalHours}h</span>
                    </div>
                    <div className="flex items-start justify-between gap-3 text-sm">
                      <span className="text-gray-500 flex-shrink-0">Working Days</span>
                      <span className="text-gray-900 text-right truncate min-w-0 tabular-nums">{record.workingDays} days</span>
                    </div>
                    <div className="flex items-start justify-between gap-3 text-sm">
                      <span className="text-gray-500 flex-shrink-0">Hours to Limit</span>
                      <span className="text-right truncate min-w-0 tabular-nums">
                        {record.isAbove72Hours ? (
                          <span className="text-red-600 font-medium">Exceeded</span>
                        ) : (
                          <span className="text-gray-900">{record.thresholdHours}h</span>
                        )}
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Desktop table view */}
            <div className="hidden md:block overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="bg-gray-50">
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-600">Employee</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-600">Department</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-600">Trade</th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-600">OT Hours</th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-600">Working Days</th>
                    <th className="px-6 py-3 text-center text-xs font-medium text-gray-600">Status</th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-600">Hours to Limit</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {reportData.records?.map(record => (
                    <tr key={record.employeeId} className="table-row">
                      <td className="px-6 py-4">
                        <div>
                          <p className="text-sm font-medium text-gray-900">{record.employeeName}</p>
                          <p className="text-xs text-gray-500">{record.employeeId}</p>
                        </div>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-900">{record.department}</td>
                      <td className="px-6 py-4 text-sm text-gray-500">{record.trade}</td>
                      <td className="px-6 py-4 text-sm text-gray-900 text-right font-medium">
                        {record.totalHours}h
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-500 text-right">
                        {record.workingDays} days
                      </td>
                      <td className="px-6 py-4 text-center">
                        <StatusBadge status={record.complianceStatus} showIcon />
                      </td>
                      <td className="px-6 py-4 text-sm text-right">
                        {record.isAbove72Hours ? (
                          <span className="text-red-600 font-medium">Exceeded</span>
                        ) : (
                          <span className="text-gray-900">{record.thresholdHours}h</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Shared empty state */}
            {(!reportData.records || reportData.records.length === 0) && (
              <div className="p-12 text-center text-gray-500">
                No records found for the selected filters
              </div>
            )}
          </div>
        </div>
      ) : (
        // Empty state
        <div className="bg-white rounded-2xl shadow-sm border p-12 text-center">
          <FileText className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No Report Generated</h3>
          <p className="text-sm text-gray-500 mb-6">
            Select your filters and click "Generate Report" to create a compliance report
          </p>
          <button
            onClick={handleGenerateReport}
            disabled={loading}
            className="inline-flex items-center px-6 py-3 text-sm font-medium text-white bg-red-600 rounded-full hover:bg-red-700 transition-colors disabled:opacity-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-500 focus-visible:ring-offset-2"
          >
            {loading ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Download className="w-4 h-4 mr-2" />
            )}
            Generate Report
          </button>
        </div>
      )}
    </div>
  );
};

export default ComplianceReport;

